import * as THREE from 'three';
import { RAIL_RADIUS } from './gearGeometry3d.js';
import { gridToWorld } from './Grid3DLayout.js';
import type { SceneTheme3D } from './sceneTheme3d.js';

const RAIL_Y_OFFSET = -0.03;
const UNIT_RAIL_HEIGHT = 1;

export class GearRailsMesh {
  readonly group = new THREE.Group();
  private railMeshes: THREE.Mesh[] = [];
  private sharedGeo: THREE.CylinderGeometry | null = null;
  private sharedMat: THREE.MeshStandardMaterial | null = null;

  build(size: number, theme: SceneTheme3D): void {
    this.clear();

    this.sharedGeo = new THREE.CylinderGeometry(RAIL_RADIUS, RAIL_RADIUS, UNIT_RAIL_HEIGHT, 8);
    this.sharedMat = new THREE.MeshStandardMaterial({
      color: theme.railColor,
      roughness: 0.55,
      metalness: 0.6,
      emissive: 0x000000,
      emissiveIntensity: 0,
    });

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (col < size - 1) {
          this.addRail(col, row, col + 1, row, size, this.sharedMat);
        }
        if (row < size - 1) {
          this.addRail(col, row, col, row + 1, size, this.sharedMat);
        }
      }
    }
  }

  private addRail(
    c1: number,
    r1: number,
    c2: number,
    r2: number,
    size: number,
    mat: THREE.MeshStandardMaterial,
  ): void {
    if (!this.sharedGeo) return;

    const start = gridToWorld(c1, r1, size);
    const end = gridToWorld(c2, r2, size);
    start.y += RAIL_Y_OFFSET;
    end.y += RAIL_Y_OFFSET;

    const mid = start.clone().add(end).multiplyScalar(0.5);
    const length = start.distanceTo(end);

    const rail = new THREE.Mesh(this.sharedGeo, mat);
    rail.position.copy(mid);
    rail.scale.set(1, length, 1);
    rail.lookAt(end);
    rail.rotateX(Math.PI / 2);
    rail.receiveShadow = true;
    this.group.add(rail);
    this.railMeshes.push(rail);
  }

  applyTheme(theme: SceneTheme3D): void {
    for (const rail of this.railMeshes) {
      const mat = rail.material as THREE.MeshStandardMaterial;
      mat.color.setHex(theme.railColor);
    }
  }

  private clear(): void {
    for (const rail of this.railMeshes) {
      this.group.remove(rail);
    }
    this.railMeshes = [];
    this.sharedGeo?.dispose();
    this.sharedGeo = null;
    this.sharedMat?.dispose();
    this.sharedMat = null;
  }

  dispose(): void {
    this.clear();
  }
}
